import React, { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { AuthUser } from '@aws-amplify/auth';
import { useAuth } from '../../../hooks/useAuth'; // Adjust the import path as needed
import { User, AccessLevel, ContributorType } from 'shared/types/platformTypes';
import { identifyUser, clearUserIdentity } from 'shared/lib/eventTracking/amplitudeTracker';

interface AuthContextType {
  showAuth: { show: boolean; signIn: boolean };
  setShowAuth: (showAuth: { show: boolean; signIn: boolean }) => void;
  currentUser: AuthUser | null;
  userProfile: User | null;
  logout: () => Promise<void>;
  fetchUserData: (force?: boolean) => Promise<void>;
  loading: boolean;
  // Access control fields
  accessLevel: AccessLevel;
  contributorType: ContributorType;
  isContributor: boolean;
  isMember: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  showAuth: { show: false, signIn: false },
  setShowAuth: () => {},
  currentUser: null,
  userProfile: null,
  logout: async () => {},
  fetchUserData: async () => {},
  loading: true,
  // Access control defaults
  accessLevel: 'member',
  contributorType: null,
  isContributor: false,
  isMember: false,
});

export const useAuthContext = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { currentUser2, userProfile, loading, logout2, showAuth, setShowAuth, fetchUserData } = useAuth();

  // Calculate access level based on user profile data
  const { accessLevel, contributorType, isContributor, isMember } = useMemo(() => {
    // Not authenticated - no access
    if (!currentUser2 || !userProfile) {
      return {
        accessLevel: 'member' as AccessLevel,
        contributorType: null as ContributorType,
        isContributor: false,
        isMember: false,
      };
    }

    // User is authenticated - at least member level
    const isMember = true;

    // Use stored access level if available (from backend calculation)
    if (userProfile.accessLevel && userProfile.contributorType !== undefined) {
      return {
        accessLevel: userProfile.accessLevel,
        contributorType: userProfile.contributorType,
        isContributor: userProfile.accessLevel === 'contributor',
        isMember,
      };
    }

    // Backend should have calculated access level - default to member if missing
    return {
      accessLevel: 'member' as AccessLevel,
      contributorType: null as ContributorType,
      isContributor: false,
      isMember,
    };
  }, [currentUser2, userProfile]);

  useEffect(() => {
    if (currentUser2) {
      identifyUser(currentUser2.userId);
    } else {
      clearUserIdentity();
    }
  }, [currentUser2]);

  const logout = async () => {
    await logout2();
    clearUserIdentity();
  };  

  return (
    <AuthContext.Provider 
      value={{ 
        currentUser: currentUser2, 
        userProfile, 
        loading, 
        logout, 
        showAuth, 
        setShowAuth, 
        fetchUserData,
        accessLevel,
        contributorType,
        isContributor,
        isMember
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
